import { CommonModule, registerLocaleData } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { OfferHelper } from '@features/offer/helpers';
import { OfferModule } from '@features/offer/offer.module';
import { OfferSettingService } from '@features/offer/services';
import { PaymentInitEffects } from '@features/payment/effects/payment-init.effects';
import { UserPreferenceHeaderInterceptor } from '@features/user-preference/interceptors/user-preference-header/user-preference-header.interceptor';
import { provideUnleashProxy } from '@karelics/angular-unleash-proxy-client';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DayJsConfig } from '@rd-lib/dayjs.config';
import { RdLibModule } from '@rd-lib/rd-lib.module';
import { RdLibModuleHermesModeConfig } from '@rd-lib/rd-lib.module.config';
import { RD_UNLEASH_CONFIG } from '@rd-lib/unleash-configs';
import { NullUnleashFacade } from '@rd/core/facades/unleash-facade/null-unleash-facade';
import { UnleashFacade } from '@rd/core/facades/unleash-facade/unleash-facade.ts';
import { NydusNetworkAuthHeaderInterceptor } from '@rd/core/interceptors/nydus-network-auth-header/nydus-network-auth-header.interceptor';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { AppSettingService } from '@rd/core/services/app-setting/app-setting.service';
import { PaymentGatewayService } from '@rd/core/services/payment-gateway/payment-gateway.service';
import { PopupSettingService } from '@rd/popup/services/popup-setting/popup-setting.service';
import { SharedModule } from '@rd/shared-legacy/shared.module';
import { WestpacAppComponent } from '@rd/wl/westpac/app.component';
import { WestpacAppSettingService } from '@rd/wl/westpac/services/app-setting/app-setting.service';
import { WestpacOfferSettingService } from '@rd/wl/westpac/services/offer-setting.service';
import { WestpacPaymentGatewayService } from '@rd/wl/westpac/services/payment-gateway/payment-gateway.service';
import { WestpacPopupSettingService } from '@rd/wl/westpac/services/popup-setting/popup-setting.service';
import { WestpacModule } from '@rd/wl/westpac/westpac.module';
import dayjs from 'dayjs';
import dayjsLocaleEn from 'dayjs/locale/en';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import { externalModules } from '../build-specifics';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { StgAnalyticsService } from './core/services/analytics/analytics.service';
import { reducers } from './store';

registerLocaleData(localeEn, 'en-AU');
dayjs.extend(relativeTime, DayJsConfig.relativeTimeConfig);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.updateLocale('en', { ...dayjsLocaleEn });

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(
    http,
    `${environment.translationBaseUrl}/`,
    '.json',
  );
}

// copied from bootstrap api
const supportedLocales: string[] = ['en-AU'];

@NgModule({
  imports: [
    CommonModule,
    BrowserModule,
    SharedModule,
    RdLibModule.forRoot({
      defaultCurrencyCode: 'AUD',
      defaultLocaleCode: 'en-AU',
      nydusNetworkApiBaseUrl: environment.nydusNetworkApiBaseUrl,
      env: environment.env,
      production: environment.production,
      guardhouseApiUrl: environment.guardhouseApiUrl,
      clientId: environment.clientId,
      connection: environment.connection,
      scope: environment.scope,
      redirectUri: environment.redirectUri,
      travelEdgeBaseUrl: environment.travelEdgeBaseUrl,
      complimentaryNightBaseUrl: null,
      brandName: 'St.George Bank',
      portalName: 'My Offers Hub',
      shortBrandName: 'STG',
      stripeApiKey: environment.stripeApiKey,
      tenantName: 'stg',
      hermesMode: environment.hermesMode as RdLibModuleHermesModeConfig,
      idleCheckConfig: environment.idleCheckConfig,
      sessionCheckPollInterval: environment.sessionCheckPollInterval,
      cookiesConfig: environment.cookiesConfig,
      supportedLocales,
      pointsTransactionEnabled: false,
      useCustomPageTitle: true,
      appId: environment.appId,
    }),
    AppRoutingModule,
    OfferModule,
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([PaymentInitEffects]),
    externalModules,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    WestpacModule,
  ],
  providers: [
    { provide: AnalyticsService, useClass: StgAnalyticsService },
    { provide: AppSettingService, useClass: WestpacAppSettingService },
    { provide: PopupSettingService, useClass: WestpacPopupSettingService },
    { provide: PaymentGatewayService, useClass: WestpacPaymentGatewayService },
    { provide: UnleashFacade, useClass: NullUnleashFacade },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NydusNetworkAuthHeaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UserPreferenceHeaderInterceptor,
      multi: true,
    },
    OfferHelper,
    { provide: OfferSettingService, useClass: WestpacOfferSettingService },
    provideUnleashProxy({
      ...RD_UNLEASH_CONFIG,
      appName: environment.unleashAppName,
      clientKey: environment.unleashClientKey,
    }),
  ],
  bootstrap: [WestpacAppComponent],
})
export class AppModule {}
